import { useState } from 'react';
import { CASE_FISCAL_TYPES, HasMlReturnInvoice } from '../../constants/pedidoTags';
import api from '../../services/api';
import { FormatDateTimeBr } from '../../utils/date';
import { FormatMoney } from '../../utils/format';

const BUTTON =
    'border border-cyan-700 px-2.5 py-1 text-xs font-semibold text-cyan-800 disabled:opacity-50 dark:border-cyan-500 dark:text-cyan-300';
const INPUT = 'border border-mono-300 bg-transparent px-2 py-1 text-sm dark:border-mono-700';

/** Seleção de nota já emitida no Bling; o servidor relê e valida antes do vínculo. */
export function CaseBlingFiscalAction({ occ, type, needed, canFinance, toast, onMarked, renderDocument }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [listing, setListing] = useState(null);
    const [preview, setPreview] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const meta = CASE_FISCAL_TYPES[type];
    if (!meta || !occ) return null;
    const hasDoc = Boolean((type === 'ENTRADA' && HasMlReturnInvoice(occ)) || occ[meta.fields.at] || result);
    if (!hasDoc && !needed) return null;

    const run = async (request) => {
        setBusy(true);
        setError('');
        try {
            await request();
        } catch (err) {
            setError(err.message || 'Não foi possível consultar a nota no Bling.');
        } finally {
            setBusy(false);
        }
    };
    const search = (page = 1) =>
        run(async () => {
            setPreview(null);
            setConfirmed(false);
            setListing(
                await api.searchCaseBlingInvoices(occ.id, { type, ...(query.trim() ? { q: query.trim() } : {}), page }),
            );
        });
    const select = (invoice) =>
        run(async () => {
            setPreview(null);
            setConfirmed(false);
            setPreview(await api.previewCaseBlingInvoice(occ.id, invoice.id, { type }));
        });
    const link = () =>
        run(async () => {
            const dto = await api.linkCaseBlingInvoice(occ.id, {
                type,
                invoice_id: preview.id,
                confirm_match: confirmed,
            });
            setResult({ number: dto[meta.fields.number], key: dto[meta.fields.key] });
            setOpen(false);
            toast?.success(`${meta.label} vinculada ao caso.`);
            await onMarked?.();
        });
    return (
        <div className="flex min-w-0 flex-col gap-2">
            {hasDoc ? (
                renderDocument(result)
            ) : (
                <span className="text-xs text-cyan-800 dark:text-cyan-300">
                    {meta.label} pendente — emitir no Bling e vincular aqui.
                </span>
            )}
            {!hasDoc && canFinance ? (
                <button
                    type="button"
                    className={`${BUTTON} self-start`}
                    disabled={busy}
                    onClick={() => {
                        setOpen(!open);
                        setError('');
                    }}
                >
                    Vincular {type === 'PERDA' ? 'NF de perda' : 'NF de devolução'} do Bling
                </button>
            ) : null}
            {!hasDoc && canFinance && open ? (
                <div className="flex flex-col gap-3 border border-cyan-600/40 p-3">
                    <form
                        onSubmit={(event) => {
                            event.preventDefault();
                            search();
                        }}
                        className="flex flex-wrap gap-2"
                    >
                        <label className="flex flex-1 flex-col gap-1 text-xs">
                            Número ou chave da nota
                            <input
                                className={INPUT}
                                inputMode="numeric"
                                value={query}
                                maxLength={44}
                                onChange={(event) => {
                                    setQuery(event.target.value);
                                    setListing(null);
                                    setPreview(null);
                                    setConfirmed(false);
                                }}
                                disabled={busy}
                                placeholder="Em branco: buscar pelo pedido"
                            />
                        </label>
                        <button type="submit" disabled={busy} className={`${BUTTON} self-end`}>
                            {busy ? 'Consultando…' : 'Buscar no Bling'}
                        </button>
                    </form>
                    {listing?.invoices.length === 0 ? (
                        <p className="text-xs text-mono-500">
                            Nenhuma nota encontrada. Tente o número ou a chave de acesso.
                        </p>
                    ) : null}
                    {listing?.invoices.map((invoice) => (
                        <button
                            key={invoice.id}
                            type="button"
                            disabled={busy}
                            onClick={() => select(invoice)}
                            className="border-b border-mono-200 py-2 text-left text-xs dark:border-mono-700"
                        >
                            NF {invoice.number} · {FormatDateTimeBr(invoice.issued_at)} · {invoice.contact || 'Bling'}
                            <span className="block break-all font-mono text-[10px] text-mono-500">
                                {invoice.key || 'Sem chave de acesso'}
                            </span>
                        </button>
                    ))}
                    {listing && (listing.page > 1 || listing.has_more) ? (
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={busy || listing.page <= 1}
                                className={BUTTON}
                                onClick={() => search(listing.page - 1)}
                            >
                                Anterior
                            </button>
                            <button
                                type="button"
                                disabled={busy || !listing.has_more}
                                className={BUTTON}
                                onClick={() => search(listing.page + 1)}
                            >
                                Próxima
                            </button>
                        </div>
                    ) : null}
                    {preview ? (
                        <div className="flex flex-col gap-2 border-l-2 border-cyan-600 pl-3 text-xs">
                            <strong>
                                NF {preview.number} · {FormatMoney(preview.amount)}
                            </strong>
                            <span>
                                Emitente: {preview.issuer_document} · {preview.nature}
                            </span>
                            <span className="break-all font-mono">{preview.key}</span>
                            <ul className="list-inside list-disc">
                                {preview.items.map((item, index) => (
                                    <li key={`${item.sku}-${index}`}>
                                        {item.sku} — {item.description} · quantidade {item.quantity}
                                    </li>
                                ))}
                            </ul>
                            {preview.warning ? (
                                <p className="bg-amber-50 p-2 text-amber-900 dark:bg-amber-900/20 dark:text-amber-300">
                                    {preview.warning}
                                </p>
                            ) : null}
                            <label className="flex items-start gap-2">
                                <input
                                    type="checkbox"
                                    checked={confirmed}
                                    onChange={(event) => setConfirmed(event.target.checked)}
                                    disabled={busy}
                                />
                                Conferi os itens e confirmo que esta nota corresponde ao caso{' '}
                                {occ.external_claim_id || occ.id} deste pedido.
                            </label>
                            <button
                                type="button"
                                className={`${BUTTON} self-start`}
                                disabled={busy || !confirmed}
                                onClick={link}
                            >
                                {busy ? 'Vinculando…' : 'Confirmar vínculo'}
                            </button>
                        </div>
                    ) : null}
                </div>
            ) : null}
            {error ? (
                <span role="alert" className="text-xs text-red-700 dark:text-red-400">
                    {error}
                </span>
            ) : null}
        </div>
    );
}

/** Decisão fiscal explícita, sem modificar o checklist aguardando validação da operação. */
export function CaseLossRequest({ occ, canFinance, onChanged, toast }) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('FALTANDO');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    if (occ.nf_loss_at) return null;
    if (occ.loss_reason)
        return <p className="mt-2 whitespace-pre-wrap text-xs text-mono-500">Perda registrada: {occ.loss_note}</p>;
    if (!canFinance) return null;
    return (
        <div className="mt-2 flex flex-col gap-2">
            <button type="button" className={`${BUTTON} self-start`} disabled={busy} onClick={() => setOpen(!open)}>
                Registrar necessidade de NF de perda
            </button>
            {open ? (
                <form
                    className="flex flex-col gap-2"
                    onSubmit={async (event) => {
                        event.preventDefault();
                        setBusy(true);
                        setError('');
                        try {
                            await api.requestCaseLossInvoice(occ.id, { reason, note: note.trim() });
                            toast?.success('NF de perda incluída nas pendências.');
                            setOpen(false);
                            await onChanged?.();
                        } catch (err) {
                            setError(err.message || 'Não foi possível registrar a perda.');
                        } finally {
                            setBusy(false);
                        }
                    }}
                >
                    <label className="flex flex-col gap-1 text-xs">
                        Motivo
                        <select
                            className={INPUT}
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            disabled={busy}
                        >
                            <option value="FALTANDO">Item ou componente faltando</option>
                            <option value="RASGADO">Item ou embalagem rasgado</option>
                            <option value="ABERTO">Item ou embalagem aberto</option>
                            <option value="OUTRO">Outro motivo</option>
                        </select>
                    </label>
                    <textarea
                        className={INPUT}
                        rows={2}
                        maxLength={2000}
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        disabled={busy}
                        aria-label="Evidência da perda"
                        placeholder="Descreva a perda e a evidência da mediação ou do recebimento"
                    />
                    <button className={`${BUTTON} self-start`} type="submit" disabled={busy || note.trim().length < 3}>
                        {busy ? 'Salvando…' : 'Registrar perda'}
                    </button>
                </form>
            ) : null}
            {error ? (
                <span role="alert" className="text-xs text-red-700">
                    {error}
                </span>
            ) : null}
        </div>
    );
}
